import { NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, ViewContainerRef, effect, forwardRef, inject, input } from '@angular/core';

import { ChartTypes, EChartTypes, LegacyChartTypes, MapTypes, TTChartTypes } from '@23degrees/shared-code/config/constants';
import { BackgroundBucketPipe } from '@23degrees/shared/pipes/background.pipe';

import { ContentSelector2Component } from '../content-selector2/content-selector2.component';
import { LayoutButtonComponent } from '../layout/layout-button/layout-button.component';
import { LayoutChartComponent } from '../layout/layout-chart/layout-chart.component';
import { LayoutDashComponent } from '../layout/layout-dash/layout-dash.component';
import { LayoutDescriptionComponent } from '../layout/layout-description/layout-description.component';
import { LayoutEChartsComponent } from '../layout/layout-echarts/layout-echarts.component';
import { LayoutElementComponent } from '../layout/layout-element/layout-element.component';
import { LayoutFilterAnnotationComponent } from '../layout/layout-filter-annotation/layout-filter-annotation.component';
import { LayoutFilterComponent } from '../layout/layout-filter/layout-filter.component';
import { LayoutInfoComponent } from '../layout/layout-info/layout-info.component';
import { LayoutLegendComponent } from '../layout/layout-legend/layout-legend.component';
import { LayoutLogoComponent } from '../layout/layout-logo/layout-logo.component';
import { LayoutMapAttributionsComponent } from '../layout/layout-map-attributions/layout-map-attributions.component';
import { LayoutMapLayersLegendComponent } from '../layout/layout-map-layers-legend/layout-map-layers-legend.component';
import { LayoutMapLegendComponent } from '../layout/layout-map-legend/layout-map-legend.component';
import { LayoutMapComponent } from '../layout/layout-map/layout-map.component';
import { LayoutMenuComponent } from '../layout/layout-menu/layout-menu.component';
import { LayoutNavigationComponent } from '../layout/layout-navigation/layout-navigation.component';
import { LayoutOverlayComponent } from '../layout/layout-overlay/layout-overlay.component';
import { LayoutSearchComponent } from '../layout/layout-search/layout-search.component';
import { LayoutSourceComponent } from '../layout/layout-source/layout-source.component';
import { LayoutSubTitleComponent } from '../layout/layout-subtitle/layout-subtitle.component';
import { LayoutTableComponent } from '../layout/layout-table/layout-table.component';
import { LayoutTitleComponent } from '../layout/layout-title/layout-title.component';
import { LayoutTTChartsComponent } from '../layout/layout-tt-charts/layout-tt-charts.component';

import type { LayoutChild } from '@23degrees/shared-code/domain/content/entities/valueobjects/Layout';
import type { Content } from '@23degrees/shared-code/models';
import type { IContentSelectorOptions, ISmartConfig } from '../index';

// const fixChangeDetectionInDev = isDevMode() ? ChangeDetectionStrategy.OnPush : ChangeDetectionStrategy.Default;

@Component({
  selector: 'layout-container',
  templateUrl: 'layout-container.component.html',
  styleUrls: ['./layout-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LayoutButtonComponent,
    LayoutChartComponent,
    LayoutDashComponent,
    LayoutDescriptionComponent,
    LayoutEChartsComponent,
    LayoutFilterAnnotationComponent,
    LayoutFilterComponent,
    LayoutInfoComponent,
    LayoutLegendComponent,
    LayoutLogoComponent,
    LayoutMapAttributionsComponent,
    LayoutMapComponent,
    LayoutMapLayersLegendComponent,
    LayoutMapLegendComponent,
    LayoutMenuComponent,
    LayoutNavigationComponent,
    LayoutOverlayComponent,
    LayoutSearchComponent,
    LayoutSourceComponent,
    LayoutSubTitleComponent,
    LayoutTableComponent,
    LayoutTitleComponent,
    LayoutTTChartsComponent,
    forwardRef(() => ContentSelector2Component),
  ],
  providers: [BackgroundBucketPipe],
})
export class LayoutContainerComponent extends LayoutElementComponent {
  public config = input.required<ISmartConfig>();
  public content = input.required<Content>();
  public options = input.required<IContentSelectorOptions>();

  public activeChild = input<{ slug: string }>();

  public isChart = (type: string) => {
    return ChartTypes.includes(type);
  };

  public isDash = (type: string) => {
    return ['dash'].includes(type);
  };

  public isLegacyChart = (type: string) => {
    return LegacyChartTypes.includes(type);
  };

  public isTTChartType = (type: string) => {
    return TTChartTypes.includes(type);
  };

  public isEChartType = (type: string) => {
    return EChartTypes.includes(type);
  };

  public isMap = (type: string) => {
    return MapTypes.includes(type);
  };

  public isTable = (type: string) => {
    return ['table'].includes(type);
  };
}
